<template>
  <main class="call notranslate" translate="no" lang="pt-BR">
    <!-- ═══ AGUARDANDO HORÁRIO (acesso direto antes da janela) ═══ -->
    <template v-if="isWaitingForSchedule">
      <div class="topbar">
        <img
          class="vm-logo"
          src="../assets/images/logo-videomedi.png"
          alt="Videomedi"
        />
        <span class="ttl">Sala da consulta</span>
      </div>

      <div class="wait">
        <div class="wait-icon">
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <circle
              cx="12"
              cy="12"
              r="9"
              stroke="currentColor"
              stroke-width="2"
            />
            <path
              d="M12 7v5l3 2"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
        <div class="wait-count">{{ waitCountdownLabel }}</div>
        <div class="wait-sub">para a consulta começar</div>

        <div class="wait-when">{{ scheduledAtBadgeLabel }}</div>
        <div class="wait-help">
          Esta tela abre a consulta automaticamente no horário. Você não precisa
          recarregar.
        </div>

        <button class="btn wait-back" @click="$router.go(-1)">Voltar</button>
      </div>
    </template>

    <!-- ═══ LOADING ═══ -->
    <template v-else-if="isLoading || loadingError">
      <div class="call-loader">
        <template v-if="loadingError">
          <div class="call-loader-msg">
            <svg
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="rgba(220,38,38,.7)"
                stroke-width="2"
              />
              <path
                d="M12 8v4M12 16h.01"
                stroke="#fff"
                stroke-width="2"
                stroke-linecap="round"
              />
            </svg>
            <p>{{ loadingError }}</p>
            <div
              style="display:flex;gap:var(--space-3);flex-wrap:wrap;justify-content:center"
            >
              <button
                class="btn btn--outline"
                style="color:#fff;border-color:rgba(255,255,255,.3)"
                @click="$router.go(-1)"
              >
                Voltar
              </button>
              <button
                class="btn btn--outline"
                style="color:#fff;border-color:rgba(255,255,255,.3)"
                @click="initCall"
              >
                Tentar novamente
              </button>
            </div>
          </div>
        </template>
        <template v-else>
          <div class="call-loader-msg">
            <dot-loader />
            <p>{{ loadingStatus }}</p>
          </div>
        </template>
      </div>
    </template>

    <template v-else>
      <!-- ═══ MAIN ═══ -->
      <div class="call-main" :class="{ 'has-side': hasSide }">
        <!-- PALCO DE VÍDEO -->
        <div class="call-stage">
          <div class="call-video" :class="{ 'call-video--split': splitLayout }">
            <!-- Timer — canto superior esquerdo -->
            <div class="vtimer">
              <span class="vtimer-dot" />
              {{ elapsedTime }}
            </div>

            <!-- MAIN: participante remoto (tela grande) -->
            <div class="call-video-main">
              <daily-video
                v-if="remoteParticipant"
                :participant="remoteParticipant"
                :has-remote-participants="true"
              />
              <div v-else class="call-waiting">
                <div
                  class="avatar avatar--xl"
                  style="background:rgba(255,255,255,.1);color:#fff"
                >
                  {{ remoteInitials }}
                </div>
                <span
                  style="color:rgba(255,255,255,.55);font-size:var(--font-size-sm)"
                >
                  {{ remoteParticipantDisplayName }} · aguardando conexão…
                </span>
              </div>
            </div>

            <!-- PiP: usuário local (canto ou lado a lado no split) -->
            <div class="call-pip" :class="{ 'call-pip--split': splitLayout }">
              <daily-video
                v-if="localParticipant"
                :participant="localParticipant"
                :flipped="true"
                :has-remote-participants="hasRemoteParticipants"
              />
              <template v-else>
                <div class="avatar avatar--sm avatar--accent">
                  {{ userInitials }}
                </div>
                <span class="pip-label">Você</span>
              </template>
            </div>

            <!-- Nome do participante remoto — canto inferior esquerdo -->
            <div v-if="!splitLayout" class="who">
              <span
                class="who-dot"
                :class="{ 'who-dot--online': !!remoteParticipant }"
              />
              {{ remoteParticipantDisplayName }}
            </div>
          </div>

          <!-- CONTROLES -->
          <div class="call-controls">
            <!-- Microfone -->
            <button
              class="call-ctrl"
              :class="{ off: !localAudioOn }"
              title="Microfone"
              @click="handleLocalAudio"
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M12 2a3 3 0 0 1 3 3v6a3 3 0 0 1-6 0V5a3 3 0 0 1 3-3z"
                  stroke="currentColor"
                  stroke-width="2"
                />
                <path
                  d="M5 11a7 7 0 0 0 14 0M12 18v3"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                />
              </svg>
            </button>

            <!-- Câmera -->
            <button
              class="call-ctrl"
              :class="{ off: !localVideoOn }"
              title="Câmera"
              @click="handleLocalVideo"
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <rect
                  x="2"
                  y="6"
                  width="14"
                  height="12"
                  rx="2"
                  stroke="currentColor"
                  stroke-width="2"
                />
                <path
                  d="M16 10l6-3v10l-6-3"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linejoin="round"
                />
              </svg>
            </button>

            <!-- Dividir tela (toggle layout) -->
            <button
              class="call-ctrl"
              :class="{ on: splitLayout }"
              title="Dividir tela"
              @click="splitLayout = !splitLayout"
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <rect
                  x="2"
                  y="3"
                  width="20"
                  height="18"
                  rx="2"
                  stroke="currentColor"
                  stroke-width="2"
                />
                <line
                  x1="12"
                  y1="3"
                  x2="12"
                  y2="21"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                />
              </svg>
            </button>

            <!-- Prontuário (apenas médico) -->
            <button
              v-if="isDoctor"
              class="call-ctrl"
              :class="{ on: activeSidePanel === 'prontuario' }"
              title="Prontuário médico"
              @click="togglePanel('prontuario')"
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linejoin="round"
                />
                <path
                  d="M14 2v6h6M12 11v6M9 14h6"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                />
              </svg>
            </button>

            <!-- Prescrição (apenas médico) -->
            <button
              v-if="isDoctor"
              class="call-ctrl"
              title="Prescrição médica"
              @click="addPatientPrescription"
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                />
              </svg>
            </button>

            <!-- Atestado médico (apenas médico) -->
            <button
              v-if="isDoctor"
              class="call-ctrl"
              title="Atestado médico"
              @click="openAtestadoForm"
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M9 11l3 3L22 4"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>

            <!-- Chat -->
            <button
              class="call-ctrl"
              :class="{
                on:
                  activeSidePanel === 'chat' ||
                  activeSidePanel === 'attachments'
              }"
              title="Chat"
              @click="togglePanel('chat')"
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linejoin="round"
                />
              </svg>
            </button>

            <!-- Encerrar -->
            <button
              class="call-ctrl end"
              :title="isDoctor ? 'Encerrar' : 'Sair da sala'"
              @click="leaveCall"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M18.36 6.64a9 9 0 1 1-12.73 0M12 2v10"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                />
              </svg>
              {{ isDoctor ? "Encerrar consulta" : "Sair da sala" }}
            </button>
          </div>
        </div>

        <!-- PAINEL LATERAL -->
        <div v-if="hasSide" class="call-side">
          <!-- Tabs do médico: Prontuário | Chat | Anexos -->
          <div v-if="isDoctor" class="call-side-head">
            <span
              class="tabx"
              :class="{ on: activeSidePanel === 'prontuario' }"
              @click="activeSidePanel = 'prontuario'"
            >
              Prontuário
            </span>
            <span
              class="tabx"
              :class="{ on: activeSidePanel === 'chat' }"
              @click="activeSidePanel = 'chat'"
            >
              Chat
            </span>
            <span
              class="tabx"
              :class="{ on: activeSidePanel === 'attachments' }"
              @click="openAttachments"
            >
              Anexos
            </span>
          </div>
          <!-- Tabs do paciente: Chat | Anexos -->
          <div v-else class="call-side-head">
            <span
              class="tabx"
              :class="{ on: activeSidePanel === 'chat' }"
              @click="activeSidePanel = 'chat'"
            >
              Chat
            </span>
            <span
              class="tabx"
              :class="{ on: activeSidePanel === 'attachments' }"
              @click="openAttachments"
            >
              Anexos
            </span>
          </div>

          <!-- Prontuário -->
          <patient-record-form
            v-if="activeSidePanel === 'prontuario'"
            :room="room"
            @cancelMedicalRecord="activeSidePanel = null"
          />

          <!-- Chat -->
          <template v-if="activeSidePanel === 'chat'">
            <div class="chat-body" ref="chatBody">
              <div
                v-for="(msg, i) in chatMessages"
                :key="i"
                class="chat-msg"
                :class="{ me: msg.me }"
              >
                <span class="au">{{ msg.author }}</span>
                <span class="bub">{{ msg.text }}</span>
              </div>
              <div
                v-if="!chatMessages.length"
                style="color:rgba(255,255,255,.4);font-size:var(--font-size-sm);text-align:center;padding:var(--space-6)"
              >
                Nenhuma mensagem ainda
              </div>
            </div>
            <div class="chat-input">
              <input
                v-model="chatInput"
                placeholder="Escreva uma mensagem…"
                @keyup.enter="sendChat"
              />
              <button class="send" @click="sendChat">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                  style="display:block;flex-shrink:0"
                >
                  <path
                    d="M22 2L11 13"
                    stroke="#ffffff"
                    stroke-width="2.2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    fill="none"
                  />
                  <path
                    d="M22 2L15 22L11 13L2 9L22 2Z"
                    stroke="#ffffff"
                    stroke-width="2.2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    fill="none"
                  />
                </svg>
              </button>
            </div>
          </template>

          <!-- Anexos: receitas, exames e atestados da consulta -->
          <div v-if="activeSidePanel === 'attachments'" class="chat-body">
            <div v-if="!isDoctor" class="exam-upload-inline">
              <label class="exam-upload-inline__file-label">
                <span class="btn btn--outline btn--sm">Anexar exame</span>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  class="exam-upload-inline__input"
                  @change="onExamUploadFile"
                />
              </label>
              <span v-if="examUploadFile" class="exam-upload-inline__filename">
                {{ examUploadFile.name }}
              </span>
              <input
                v-if="examUploadFile"
                v-model="examUploadDescription"
                type="text"
                class="exam-upload-inline__desc"
                placeholder="Descrição (opcional)"
              />
              <button
                v-if="examUploadFile"
                class="btn btn--accent btn--sm"
                :disabled="uploadingExam"
                @click="submitExamUpload"
              >
                {{ uploadingExam ? "Enviando…" : "Enviar exame" }}
              </button>
              <p v-if="examUploadError" class="exam-upload-inline__error">
                {{ examUploadError }}
              </p>
            </div>
            <div
              v-for="doc in appointmentDocuments"
              :key="`${doc._type}-${doc.id}`"
              class="attach-doc"
            >
              <span
                class="attach-doc__badge"
                :class="`attach-doc__badge--${doc._type}`"
              >
                {{
                  doc._type === "receita"
                    ? "Receita"
                    : doc._type === "atestado"
                    ? "Atestado"
                    : "Exame"
                }}
              </span>
              <div class="attach-doc__info">
                <span class="attach-doc__title">{{ docTitle(doc) }}</span>
                <span class="attach-doc__date">
                  {{
                    formatDocumentDate(doc.prescription_date || doc.created_at)
                  }}
                </span>
              </div>
              <button
                type="button"
                class="attach-doc__link"
                @click="openDocumentModal(doc)"
              >
                Ver
              </button>
            </div>
            <div
              v-if="!appointmentDocuments.length"
              style="color:rgba(255,255,255,.4);font-size:var(--font-size-sm);text-align:center;padding:var(--space-6)"
            >
              Nenhum anexo disponível
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Modal Atestado médico -->
    <div
      v-if="showAtestadoForm"
      class="atestado-modal-backdrop"
      @click.self="cancelAtestadoForm"
    >
      <div class="atestado-modal">
        <div class="atestado-modal__head">
          <span>Emitir atestado médico</span>
          <button
            class="atestado-modal__close"
            @click="cancelAtestadoForm"
            aria-label="Fechar"
          >
            ×
          </button>
        </div>

        <div v-if="atestadoSalvo" class="atestado-modal__success">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
            <path
              d="M20 6L9 17l-5-5"
              stroke="var(--color-success, #2e7d32)"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <span>Atestado emitido com sucesso!</span>
        </div>

        <div v-else class="atestado-form">
          <textarea
            v-model="atestadoForm.description"
            class="atestado-form__desc"
            placeholder="Descrição (ex: afastamento de 3 dias por síndrome gripal)"
          />
          <div class="atestado-form__row">
            <input
              type="number"
              v-model.number="atestadoForm.days_off"
              placeholder="Dias de afastamento"
            />
            <input
              type="text"
              v-model="atestadoForm.cid_code"
              placeholder="CID (opcional)"
            />
          </div>
          <!--
            Anexo manual de arquivo desativado: o atestado agora é gerado em PDF
            pela própria Videomedi (botão "Gerar atestado" abaixo). Mantido
            comentado caso o anexo manual volte a ser necessário.
          <label class="atestado-form__file-label">
            <span class="btn btn--outline btn--sm">Anexar arquivo</span>
            <input
              type="file"
              @change="onAtestadoFile"
              accept=".pdf,.jpg,.jpeg,.png"
              class="atestado-form__file-input"
            />
          </label>
          <span v-if="atestadoFile" class="atestado-form__filename">
            {{ atestadoFile.name }}
          </span>
          -->

          <div class="atestado-form__generate">
            <button
              type="button"
              class="btn btn--outline btn--sm"
              :disabled="generatingAtestado || !atestadoForm.description"
              @click="generateAtestadoPdf"
            >
              {{ generatingAtestado ? "Gerando…" : "Gerar atestado" }}
            </button>
          </div>

          <p v-if="atestadoError" class="atestado-form__error">
            {{ atestadoError }}
          </p>

          <p v-if="atestadoPdf" class="atestado-form__generated">
            <span>
              Atestado gerado. Clique
              <a @click="openAtestadoPdf" class="atestado-form__open-link">
                aqui
              </a>
              para revisar, antes de salvar
            </span>
          </p>

          <div class="atestado-form__actions">
            <button
              class="btn btn--ghost btn--sm"
              @click="cancelAtestadoForm"
              :disabled="savingAtestado"
            >
              Cancelar
            </button>
            <button
              class="btn btn--primary btn--sm"
              @click="createAtestado"
              :disabled="isSaveAtestadoDisabled"
            >
              {{ saveAtestadoLabel }}
            </button>
          </div>

          <!-- Modelo usado só para gerar o PDF do atestado (renderizado fora da tela). -->
          <div ref="atestadoDoc" class="atestado-doc" aria-hidden="true">
            <img
              src="../assets/images/logo-videomedi.png"
              alt="Videomedi"
              class="atestado-doc__logo"
            />
            <div class="atestado-doc__rule" />
            <h3 class="atestado-doc__title">ATESTADO MÉDICO</h3>
            <p class="atestado-doc__body">
              Atesto, para os devidos fins, que o(a) paciente
              <strong>{{ atestadoPatientName }}</strong>
              esteve sob meus cuidados médicos nesta data
              <span v-if="atestadoForm.days_off">
                , necessitando de
                <strong>{{ atestadoForm.days_off }} dia(s)</strong>
                de afastamento de suas atividades habituais
              </span>
              .
            </p>
            <p v-if="atestadoForm.description" class="atestado-doc__field">
              <span class="atestado-doc__label">Observações</span>
              {{ atestadoForm.description }}
            </p>
            <p v-if="atestadoForm.cid_code" class="atestado-doc__field">
              <span class="atestado-doc__label">CID</span>
              {{ atestadoForm.cid_code }}
            </p>
            <p class="atestado-doc__date">
              Emitido em {{ atestadoTodayLabel }}
            </p>
            <div class="atestado-doc__sign">
              <span class="atestado-doc__sign-name">
                {{ atestadoDoctorName }}
              </span>
              <span
                v-if="atestadoDoctorCredentials"
                class="atestado-doc__sign-role"
              >
                {{ atestadoDoctorCredentials }}
              </span>
            </div>
            <div class="atestado-doc__foot">
              Videomedi · CNPJ 00.000.000/0001-00
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de visualização de documento (receita, exame ou atestado) -->
    <document-view-modal ref="documentViewModal" />
  </main>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { createVideoCallProvider } from "@/services/videoCall";
import chatMessagesService from "@/services/videoCall/chatMessages";
import { isDoctor } from "../utils/userRole";
import { isBeforeCallStart, formatCallCountdown } from "../utils/roomEntryLock";
import { MedicalCertificates } from "../services/medical_certificates";
import { Appointments } from "../services/appointments";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

import DailyVideo from "../components/DailyVideo.vue";
import PatientRecordForm from "../components/PatientRecordForm";
import DotLoader from "../components/DotLoader.vue";
import DocumentViewModal from "../components/DocumentViewModal.vue";

export default {
  name: "MeetingRoomView",
  components: {
    DailyVideo,
    PatientRecordForm,
    DotLoader,
    DocumentViewModal
  },
  data() {
    return {
      isLoading: false,
      loadingStatus: "",
      loadingError: null,
      isWaitingForSchedule: false,
      nowTs: Date.now(),
      waitTimer: null,
      activeSidePanel: null,
      splitLayout: false,
      room: null,
      videoCall: null,
      participants: null,
      localParticipant: null,
      prescriptionChannel: null,
      callSeconds: 0,
      timerInterval: null,
      chatInput: "",
      chatMessages: [],
      showAtestadoForm: false,
      atestadoSalvo: null,
      savingAtestado: false,
      atestadoForm: {
        description: "",
        days_off: null,
        cid_code: ""
      },
      atestadoFile: null,
      // PDF do atestado gerado pela própria Videomedi (data URI base64). É o
      // documento principal: sem ele o "Salvar" fica bloqueado.
      atestadoPdf: null,
      // blob URL do mesmo PDF, usada só para abrir em nova aba (o Chrome
      // bloqueia window.open com data: de PDF; blob abre no visualizador).
      atestadoPdfUrl: null,
      generatingAtestado: false,
      atestadoError: null,
      examUploadFile: null,
      examUploadDescription: "",
      uploadingExam: false,
      examUploadError: null
    };
  },
  mounted() {
    this.initCall();
  },
  beforeDestroy() {
    this.teardownCall();
    this.revokeAtestadoPdfUrl();
  },
  methods: {
    ...mapActions("room", ["getRoomDetailsV2"]),
    ...mapActions("prescriptions", [
      "fetchAppointmentPrescriptions",
      "fetchAppointmentExamAttachments",
      "fetchAppointmentMedicalCertificates",
      "fetchAppointmentPrescriptionsForPatient",
      "fetchAppointmentExamAttachmentsForPatient",
      "fetchAppointmentMedicalCertificatesForPatient"
    ]),

    subscribeToCallEvents() {
      this.videoCall.on("joining", this.handleJoiningMeeting);
      this.videoCall.on("joined", this.handleJoined);
      this.videoCall.on("participant-joined", this.updateParticpants);
      this.videoCall.on("participant-updated", this.updateParticpants);
      this.videoCall.on("participant-left", this.updateParticpants);
      this.videoCall.on("message", this.handleIncomingMessage);
    },
    unsubscribeFromCallEvents() {
      if (!this.videoCall) return;
      this.videoCall.off("joining", this.handleJoiningMeeting);
      this.videoCall.off("joined", this.handleJoined);
      this.videoCall.off("participant-joined", this.updateParticpants);
      this.videoCall.off("participant-updated", this.updateParticpants);
      this.videoCall.off("participant-left", this.updateParticpants);
      this.videoCall.off("message", this.handleIncomingMessage);
    },

    addPatientPrescription() {
      // Abre a prescrição em uma aba própria (PrescriptionView), que carrega o
      // SDK Memed no seu próprio documento. A aba nova herda a sessão (auth)
      // via sessionStorage clonado pelo navegador — por isso o window.open NÃO
      // pode usar "noopener" (veja docs/design/flow-d05-receita.md).
      const { href } = this.$router.resolve({
        name: "PrescriptionView",
        params: { room: this.$route.params.room }
      });
      window.open(href, "_blank");
    },

    // Desfaz qualquer chamada anterior (listeners, timer, canal e o call object
    // do Daily) antes de reinicializar. Sem isso, o "Tentar novamente" cai num
    // segundo daily.createCallObject() e o SDK lança "Duplicate DailyIframe
    // instances are not allowed" — throw síncrono que o .catch de initCall
    // engole e reexibe como "Não foi possível carregar a sala".
    clearWaitTimer() {
      if (this.waitTimer) {
        clearInterval(this.waitTimer);
        this.waitTimer = null;
      }
      this.isWaitingForSchedule = false;
    },

    teardownCall() {
      this.clearWaitTimer();
      this.unsubscribeFromCallEvents();
      if (this.videoCall) {
        try {
          this.videoCall.destroy();
        } catch (err) {
          console.error("Video call teardown error:", err);
        }
        this.videoCall = null;
      }
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
        this.timerInterval = null;
      }
      if (this.prescriptionChannel) {
        this.prescriptionChannel.close();
        this.prescriptionChannel = null;
      }
      this.participants = null;
      this.localParticipant = null;
      this.callSeconds = 0;
    },

    initCall() {
      this.teardownCall();
      this.isLoading = true;
      this.loadingError = null;
      this.loadingStatus = "Buscando informações da sala…";

      this.getRoomDetailsV2(this.$route.params.room)
        .then(response => {
          this.room = response.data;

          // Acesso direto (link copiado, app, URL salva) antes da janela:
          // mostra o contador em vez de tentar entrar na chamada. Vale igual
          // para médico e paciente — sem exceção de papel.
          if (isBeforeCallStart(this.room.scheduled_at)) {
            this.enterWaitingForSchedule();
            return;
          }

          this.joinCall();
        })
        .catch(err => {
          this.isLoading = false;
          this.loadingError =
            "Não foi possível carregar a sala. Tente novamente.";
          console.error("getRoomDetailsV2 error:", err);
        });
    },

    joinCall() {
      this.loadingStatus = "Conectando à videochamada…";
      this.subscribeToPrescriptionChannel();

      const options = {
        url: this.room.url,
        startAudioOff: this.$route.params.audioMuted,
        startVideoOff: this.$route.params.videoMuted
      };

      this.videoCall = createVideoCallProvider();
      this.videoCall.init(options);
      this.videoCall.on("error", this.handleCallError);
      this.videoCall.join(this.getCurrentUser?.name).catch(err => {
        this.isLoading = false;
        this.loadingError =
          "Não foi possível entrar na sala. Verifique sua conexão e tente novamente.";
        console.error("Video call join error:", err);
      });
      this.subscribeToCallEvents();
    },

    // Reaproveita o mesmo padrão de setInterval de WaitingRoomView.vue: sem
    // watch novo, sem reload. Ao cruzar a janela, entra na chamada sozinho.
    enterWaitingForSchedule() {
      this.isLoading = false;
      this.isWaitingForSchedule = true;
      this.nowTs = Date.now();
      this.waitTimer = setInterval(() => {
        this.nowTs = Date.now();
        if (!isBeforeCallStart(this.room?.scheduled_at, this.nowTs)) {
          this.clearWaitTimer();
          this.isLoading = true;
          this.joinCall();
        }
      }, 1000);
    },

    handleCallError(event) {
      this.isLoading = false;
      this.loadingError = "Erro de conexão com a sala. Tente novamente.";
      console.error("Video call error event:", event);
    },

    // Encerra a consulta de verdade (só o médico). Resolve para
    // { finished: true } quando deu certo (ou não havia nada a fazer) e
    // { finished: false } quando a chamada ao backend falhou — nesse caso o
    // leaveCall mostra um modal e leva o médico para a lista de consultas.
    // Objeto (sempre truthy) de propósito: um retorno `false` no preConfirm do
    // SweetAlert manteria o modal de confirmação aberto.
    finishAppointmentIfDoctor() {
      if (!this.isDoctor || !this.room || !this.room.appointment_id) {
        return Promise.resolve({ finished: true });
      }

      return Appointments.finish(this.room.appointment_id)
        .then(() => ({ finished: true }))
        .catch(err => {
          console.error("finish appointment error:", err);
          return { finished: false };
        });
    },

    afterLeaveConfirmed(result) {
      this.leaveAndCleanUp();

      if (result.finished) {
        this.leaveAndRedirect();
        return;
      }

      // Encerramento falhou: avisa e leva para a lista de consultas, onde há o
      // botão "Encerrar consulta".
      const goToList = () => this.$router.push({ name: "Dashboard" });
      const message =
        "A consulta não pôde ser encerrada aqui. Encerre pela lista de consultas.";

      if (typeof swal !== "undefined") {
        swal
          .fire({
            icon: "warning",
            title: "Não foi possível encerrar a consulta",
            text: message,
            confirmButtonText: "Ok",
            allowOutsideClick: false,
            allowEscapeKey: false
          })
          .then(goToList);
        return;
      }

      window.alert(message);
      goToList();
    },

    leaveCall() {
      const runLeave = () => {
        window.removeEventListener("beforeunload", this.leaveComfirm);
        return this.finishAppointmentIfDoctor();
      };

      const texts = this.isDoctor
        ? {
            title: "Encerrar consulta?",
            text:
              "Ao confirmar, a consulta será encerrada definitivamente. Nem você nem o paciente poderão reabrir esta sala.",
            icon: "warning",
            confirmButtonText: "Sim, encerrar consulta"
          }
        : {
            title: "Tem certeza de que quer sair da sala?",
            text:
              "Enquanto o médico estiver na sala, você ainda pode retornar.",
            icon: "question",
            confirmButtonText: "Sim, sair"
          };

      if (typeof swal !== "undefined") {
        return swal
          .fire({
            title: texts.title,
            text: texts.text,
            icon: texts.icon,
            showCancelButton: true,
            confirmButtonColor: "#ED135A",
            cancelButtonColor: "#cb6262",
            confirmButtonText: texts.confirmButtonText,
            cancelButtonText: "Cancelar",
            showLoaderOnConfirm: true,
            preConfirm: runLeave
          })
          .then(result => {
            if (result.isConfirmed) this.afterLeaveConfirmed(result.value);
          });
      } else if (window.confirm(texts.text)) {
        return runLeave().then(result => this.afterLeaveConfirmed(result));
      }
    },

    leaveComfirm(event) {
      const warning = "Você deseja realmente sair?";
      event.returnValue = warning;
      return warning;
    },

    leaveAndCleanUp() {
      if (!this.videoCall) return;
      this.videoCall.leave().then(() => {
        this.videoCall.destroy();
      });
    },

    leaveAndRedirect() {
      if (this.isDoctor) {
        this.$router.push({
          name: "ConsultFinished",
          params: { room: this.room.id }
        });
      } else {
        this.$router.push({ name: "Consultas" });
      }
    },

    startTimer() {
      if (this.timerInterval) return;
      this.timerInterval = setInterval(() => {
        this.callSeconds++;
      }, 1000);
    },

    handleJoiningMeeting() {
      this.isLoading = true;
    },

    handleJoined() {
      this.updateParticpants();
      if (this.room?.appointment_id) {
        chatMessagesService
          .list(this.room.appointment_id)
          .then(({ data }) => {
            this.chatMessages = data.map(m => ({
              author: m.sender_name,
              text: m.body,
              me: m.sender_name === this.getCurrentUser?.name,
              timestamp: m.created_at
            }));
            this.scrollChat();
          })
          .catch(() => {});
      }
    },

    updateParticpants() {
      if (!this.videoCall) return;
      this.participants = this.videoCall.getParticipants();
      this.localParticipant = this.participants.find(p => p.local);
      this.isLoading = false;
      this.startTimer();
    },

    handleIncomingMessage({ body, senderName }) {
      this.chatMessages.push({
        author: senderName,
        text: body,
        me: false,
        timestamp: new Date().toISOString()
      });
      this.scrollChat();
    },

    handleLocalAudio() {
      const audioOn = this.videoCall.localAudio();
      this.videoCall.setLocalAudio(!audioOn);
    },

    handleLocalVideo() {
      const audioWasOn = this.videoCall.localAudio();
      const videoOn = this.videoCall.localVideo();
      this.videoCall.setLocalVideo(!videoOn);

      const ensureAudioState = () => {
        if (audioWasOn && !this.videoCall.localAudio()) {
          this.videoCall.setLocalAudio(true);
        }
        this.videoCall.off("participant-updated", ensureAudioState);
      };
      this.videoCall.on("participant-updated", ensureAudioState);
    },

    togglePanel(panel) {
      this.activeSidePanel = this.activeSidePanel === panel ? null : panel;
    },

    openAttachments() {
      this.activeSidePanel = "attachments";
      this.refreshAppointmentDocuments();
    },

    refreshAppointmentDocuments() {
      if (!this.room || !this.room.appointment_id) return Promise.resolve();
      if (this.isDoctor) {
        return Promise.all([
          this.fetchAppointmentPrescriptions(this.room.appointment_id),
          this.fetchAppointmentExamAttachments(this.room.appointment_id),
          this.fetchAppointmentMedicalCertificates(this.room.appointment_id)
        ]);
      }
      return Promise.all([
        this.fetchAppointmentPrescriptionsForPatient(this.room.appointment_id),
        this.fetchAppointmentExamAttachmentsForPatient(
          this.room.appointment_id
        ),
        this.fetchAppointmentMedicalCertificatesForPatient(
          this.room.appointment_id
        )
      ]);
    },

    formatDocumentDate(value) {
      if (!value) return "";
      return new Date(value).toLocaleDateString("pt-BR");
    },

    docTitle(doc) {
      if (doc._type === "receita") return this.prescriptionTitle(doc);
      if (doc._type === "atestado") return doc.description || "Atestado médico";
      return doc.description || "Exame enviado";
    },

    prescriptionTitle(doc) {
      const names = (doc.drugs || []).map(d => d.name).filter(Boolean);
      return names.length ? names.join(", ") : "Memed · assinada digitalmente";
    },

    openDocumentModal(doc) {
      this.$refs.documentViewModal.open(doc, () => this.refreshDocument(doc));
    },

    async refreshDocument(doc) {
      await this.refreshAppointmentDocuments();
      return this.appointmentDocuments.find(
        d => d.id === doc.id && d._type === doc._type
      );
    },

    subscribeToPrescriptionChannel() {
      if (typeof BroadcastChannel === "undefined" || !this.room?.appointment_id)
        return;
      this.prescriptionChannel = new BroadcastChannel(
        `memed-prescription-${this.room.appointment_id}`
      );
      this.prescriptionChannel.onmessage = () =>
        this.refreshAppointmentDocuments();
    },

    scrollChat() {
      this.$nextTick(() => {
        const el = this.$refs.chatBody;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },

    sendChat() {
      const text = (this.chatInput || "").trim();
      if (!text) return;

      const senderName = this.getCurrentUser?.name || "Você";

      this.chatMessages.push({
        author: senderName,
        text,
        me: true,
        timestamp: new Date().toISOString()
      });
      this.chatInput = "";
      this.scrollChat();

      this.videoCall.sendMessage({
        body: text,
        senderName,
        senderType: this.isDoctor ? "doctor" : "patient"
      });

      if (this.room?.appointment_id) {
        chatMessagesService
          .create(this.room.appointment_id, text)
          .catch(() => {});
      }
    },

    openAtestadoForm() {
      this.atestadoSalvo = null;
      this.atestadoForm = { description: "", days_off: null, cid_code: "" };
      this.atestadoFile = null;
      this.atestadoPdf = null;
      this.revokeAtestadoPdfUrl();
      this.atestadoError = null;
      this.generatingAtestado = false;
      this.showAtestadoForm = true;
    },

    cancelAtestadoForm() {
      this.showAtestadoForm = false;
      this.revokeAtestadoPdfUrl();
    },

    openAtestadoPdf() {
      if (this.atestadoPdfUrl) window.open(this.atestadoPdfUrl, "_blank");
    },

    revokeAtestadoPdfUrl() {
      if (!this.atestadoPdfUrl) return;
      try {
        URL.revokeObjectURL(this.atestadoPdfUrl);
      } catch (e) {
        // blob URL já inválida — nada a fazer
      }
      this.atestadoPdfUrl = null;
    },

    buildAtestadoPayload() {
      const payload = { description: this.atestadoForm.description };
      if (this.atestadoForm.days_off)
        payload.days_off = this.atestadoForm.days_off;
      if (this.atestadoForm.cid_code)
        payload.cid_code = this.atestadoForm.cid_code;
      return payload;
    },

    async attachFileToPayload(payload) {
      if (this.atestadoPdf) {
        payload.file = { data: this.atestadoPdf };
      }
      return payload;
    },

    // Monta o PDF do atestado (logo + dados do formulário + identificação do
    // médico) fora da tela e guarda como data URI. Mesmo caminho do comprovante
    // de pagamento: html2canvas -> jsPDF.
    async generateAtestadoPdf() {
      if (!this.atestadoForm.description || this.generatingAtestado) return;
      this.generatingAtestado = true;
      this.atestadoError = null;
      try {
        await this.$nextTick();
        const element = this.$refs.atestadoDoc;
        const scale = 2;
        const canvas = await html2canvas(element, {
          scale,
          backgroundColor: "#ffffff"
        });
        const pdf = new jsPDF({
          unit: "mm",
          format: "a4",
          orientation: "portrait"
        });
        const imgData = canvas.toDataURL("image/jpeg", 0.98);
        const pxToMm = 25.4 / 96;
        const pageWidth = pdf.internal.pageSize.getWidth();
        const imgWidth = Math.min((canvas.width / scale) * pxToMm, 190);
        const imgHeight = (canvas.height / scale) * pxToMm;
        const x = (pageWidth - imgWidth) / 2;
        pdf.addImage(imgData, "JPEG", x, 12, imgWidth, imgHeight);
        // jsPDF devolve "data:application/pdf;filename=generated.pdf;base64,…";
        // o back (has_one_base64_attached) espera "data:application/pdf;base64,…".
        this.atestadoPdf = pdf
          .output("datauristring")
          .replace(/;filename=[^;,]+/, "");
        // blob URL só para o "abrir em nova aba" (data: de PDF o Chrome bloqueia)
        this.revokeAtestadoPdfUrl();
        this.atestadoPdfUrl = pdf.output("bloburl");
      } catch (e) {
        this.atestadoError =
          "Não foi possível gerar o atestado. Tente novamente.";
      } finally {
        this.generatingAtestado = false;
      }
    },

    async createAtestado() {
      if (!this.room?.appointment_id) return;
      // o documento é o principal: não salva sem o PDF gerado
      if (!this.atestadoPdf) return;
      this.savingAtestado = true;
      try {
        const payload = await this.attachFileToPayload(
          this.buildAtestadoPayload()
        );
        const res = await MedicalCertificates.createMedicalCertificate(
          this.room.appointment_id,
          payload
        );
        this.atestadoSalvo = res.data;
        this.refreshAppointmentDocuments();
      } finally {
        this.savingAtestado = false;
      }
    },

    onAtestadoFile(e) {
      this.atestadoFile = e.target.files[0] || null;
    },

    toBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },

    onExamUploadFile(e) {
      this.examUploadFile = e.target.files[0] || null;
      this.examUploadError = null;
    },

    async submitExamUpload() {
      if (!this.room?.appointment_id || !this.examUploadFile) return;
      this.uploadingExam = true;
      this.examUploadError = null;
      try {
        const data = await this.toBase64(this.examUploadFile);
        await Appointments.uploadExamAttachment(this.room.appointment_id, {
          description: this.examUploadDescription,
          file: { data }
        });
        this.examUploadFile = null;
        this.examUploadDescription = "";
        this.refreshAppointmentDocuments();
      } catch {
        this.examUploadError = "Erro ao enviar o exame. Tente novamente.";
      } finally {
        this.uploadingExam = false;
      }
    }
  },
  computed: {
    ...mapGetters("user", ["getCurrentUser"]),
    ...mapGetters("prescriptions", ["appointmentDocuments"]),

    isDoctor() {
      return isDoctor(this.$store.state.user.currentUser);
    },

    hasSide() {
      return this.activeSidePanel !== null;
    },

    remoteParticipant() {
      if (!this.participants) return null;
      return this.participants.find(p => !p.local) || null;
    },

    remoteParticipantDisplayName() {
      if (!this.room) return "";
      return this.isDoctor
        ? this.room.patient?.name || ""
        : this.room.doctor?.name || "";
    },

    remoteInitials() {
      return (
        (this.remoteParticipantDisplayName || "")
          .split(" ")
          .slice(0, 2)
          .map(n => n[0])
          .join("")
          .toUpperCase() || ""
      );
    },

    userInitials() {
      return (
        (this.getCurrentUser?.name || "")
          .split(" ")
          .slice(0, 2)
          .map(n => n[0])
          .join("")
          .toUpperCase() || ""
      );
    },

    hasRemoteParticipants() {
      return this.participants && this.participants.length > 1;
    },

    localAudioOn() {
      if (this.localParticipant) return !!this.localParticipant.audio;
      return this.videoCall ? this.videoCall.localAudio() : true;
    },

    localVideoOn() {
      if (this.localParticipant) return !!this.localParticipant.video;
      return this.videoCall ? this.videoCall.localVideo() : true;
    },

    elapsedTime() {
      const m = Math.floor(this.callSeconds / 60)
        .toString()
        .padStart(2, "0");
      const s = (this.callSeconds % 60).toString().padStart(2, "0");
      return `${m}:${s}`;
    },

    saveAtestadoLabel() {
      return this.savingAtestado ? "Salvando…" : "Salvar atestado";
    },

    isSaveAtestadoDisabled() {
      return this.savingAtestado || !this.atestadoPdf;
    },

    atestadoPatientName() {
      return (this.room && this.room.patient && this.room.patient.name) || "";
    },

    atestadoDoctorName() {
      return (
        (this.room && this.room.doctor && this.room.doctor.name) ||
        (this.getCurrentUser && this.getCurrentUser.name) ||
        ""
      );
    },

    atestadoDoctorCredentials() {
      const user = this.$store.state.user.currentUser;
      const dp = (user && user.doctor_property) || {};
      const council =
        dp.council_description ||
        [dp.council_name, dp.council_number, dp.council_uf]
          .filter(Boolean)
          .join(" ");
      return [council, dp.medical_specialty_name].filter(Boolean).join(" · ");
    },

    atestadoTodayLabel() {
      return new Date().toLocaleDateString("pt-BR");
    },

    waitCountdownLabel() {
      return formatCallCountdown(this.room?.scheduled_at, this.nowTs);
    },

    scheduledAtBadgeLabel() {
      if (!this.room?.scheduled_at) return "";
      const scheduled = new Date(this.room.scheduled_at);
      const time = scheduled.toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit"
      });

      const now = new Date(this.nowTs);
      const isToday = scheduled.toDateString() === now.toDateString();
      const tomorrow = new Date(now);
      tomorrow.setDate(now.getDate() + 1);
      const isTomorrow = scheduled.toDateString() === tomorrow.toDateString();

      const dayLabel = isToday
        ? "hoje"
        : isTomorrow
        ? "amanhã"
        : scheduled.toLocaleDateString("pt-BR");

      return `Consulta marcada para ${dayLabel} às ${time}`;
    }
  },
  watch: {
    // Qualquer edição nos campos invalida o PDF já gerado — ele deixa de
    // refletir o formulário, então força gerar de novo antes de salvar.
    atestadoForm: {
      deep: true,
      handler() {
        this.atestadoPdf = null;
        this.revokeAtestadoPdfUrl();
      }
    }
  }
};
</script>

<style>
/* ─── CALL SHELL ─── */
.call {
  background: #0b1220;
  height: 100vh;
  overflow: hidden;
  color: #fff;
  position: relative;
}

.call-loader {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100vh;
  background: #0b1220;
}
.call-loader-msg {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  text-align: center;
}
.call-loader-msg p {
  margin: 0;
  max-width: 280px;
}

/* ─── AGUARDANDO HORÁRIO (acesso direto antes da janela) ─── */
.topbar {
  display: flex;
  align-items: center;
  gap: var(--space-4);
  padding: var(--space-4) var(--space-8);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.topbar .vm-logo {
  display: block;
  height: 24px;
  width: auto;
}

.topbar .ttl {
  font-size: var(--font-size-sm);
  font-weight: var(--fw-bold);
  color: rgba(255, 255, 255, 0.85);
}

.wait {
  height: calc(100vh - 57px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 6px;
  max-width: 440px;
  margin: 0 auto;
  padding: var(--space-6);
}

.wait-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
}

.wait-count {
  font-family: var(--font-num, "Inter", sans-serif);
  font-size: 30px;
  font-weight: var(--fw-bold);
  color: #fff;
  letter-spacing: -0.01em;
}

.wait-sub {
  font-size: var(--font-size-md, 15px);
  color: rgba(255, 255, 255, 0.6);
}

.wait-when {
  margin-top: 18px;
  font-size: var(--font-size-sm);
  color: rgba(255, 255, 255, 0.8);
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: var(--radius-full, 9999px);
  padding: 8px 16px;
}

.wait-help {
  margin-top: 10px;
  font-size: var(--font-size-xs);
  color: rgba(255, 255, 255, 0.45);
  line-height: 1.5;
}

.wait-back {
  margin-top: var(--space-6);
  height: 44px;
  padding: 0 var(--space-6);
  border-radius: var(--radius-md);
  border: 1.5px solid rgba(255, 255, 255, 0.3);
  background: transparent;
  color: #fff;
  font-weight: var(--fw-bold);
  font-size: var(--font-size-sm);
}

/* ─── MAIN GRID ─── */
.call-main {
  height: 100vh;
  display: grid;
  grid-template-columns: 1fr;
  background: #0b1220;
}
.call-main.has-side {
  grid-template-columns: 1fr 340px;
}

/* ─── STAGE ─── */
.call-stage {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  padding: var(--space-5);
  min-width: 0;
}

/* ─── VIDEO AREA ─── */
.call-video {
  flex: 1;
  border-radius: var(--radius-xl);
  background: linear-gradient(160deg, #1e293b, #0f172a);
  position: relative;
  overflow: hidden;
}

/* ─── MAIN (remote) — fills the stage ─── */
.call-video-main {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.call-video-main .media-video {
  width: 100% !important;
  height: 100% !important;
  max-width: none !important;
  border-radius: 0 !important;
}

/* ─── WAITING placeholder ─── */
.call-waiting {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-3);
}

/* ─── VTIMER — canto superior esquerdo ─── */
.vtimer {
  position: absolute;
  left: var(--space-4);
  top: var(--space-4);
  background: rgba(220, 38, 38, 0.9);
  color: #fff;
  font-family: var(--font-num, "Inter", sans-serif);
  font-weight: var(--fw-bold);
  font-size: var(--font-size-xs);
  padding: 5px 11px;
  border-radius: var(--radius-full);
  display: flex;
  align-items: center;
  gap: 6px;
  z-index: 3;
}

.vtimer-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #fff;
  animation: blink 1.2s ease-in-out infinite;
  flex: none;
}

/* ─── PiP — canto superior direito (local) ─── */
.call-pip {
  position: absolute;
  right: var(--space-4);
  top: var(--space-4);
  width: 180px;
  height: 120px;
  border-radius: var(--radius-md);
  background: #0b1220;
  border: 2px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.call-pip .media-video {
  width: 100% !important;
  height: 100% !important;
  max-width: none !important;
  border-radius: 0 !important;
}

.pip-label {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.6);
}

/* ─── WHO — canto inferior esquerdo ─── */
.who {
  position: absolute;
  left: var(--space-4);
  bottom: var(--space-4);
  background: rgba(0, 0, 0, 0.5);
  color: #fff;
  font-size: var(--font-size-sm);
  font-weight: var(--fw-semibold);
  padding: 6px 12px;
  border-radius: var(--radius-full);
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 2;
}

.who-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  flex: none;
}

.who-dot--online {
  background: var(--color-success, #16a34a);
}

/* ─── SPLIT LAYOUT ─── */
.call-video--split {
  display: flex;
  gap: var(--space-3);
  padding: var(--space-3);
}

.call-video--split .call-video-main {
  position: static;
  flex: 1;
  border-radius: var(--radius-lg);
  overflow: hidden;
}

.call-video--split .call-video-main .media-video {
  border-radius: 0 !important;
}

.call-video--split .call-pip {
  position: static;
  flex: 1;
  width: auto;
  height: auto;
  border-radius: var(--radius-lg);
  border: 2px solid rgba(255, 255, 255, 0.15);
}

/* ─── CONTROLS ─── */
.call-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-3);
  flex-shrink: 0;
}

.call-ctrl {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s;
  border: none;
  flex: none;
}

.call-ctrl svg {
  display: block;
  flex: none;
}

.call-ctrl:hover,
.call-ctrl:focus,
.call-ctrl:active {
  background: rgba(255, 255, 255, 0.22);
  outline: none;
  box-shadow: none;
}

.call-ctrl.off {
  background: var(--color-danger, #dc2626);
}

.call-ctrl.on {
  background: rgba(255, 255, 255, 0.28);
}

.call-ctrl.end {
  width: auto;
  border-radius: var(--radius-full);
  background: var(--color-danger, #dc2626);
  padding: 0 var(--space-6);
  gap: 8px;
  font-weight: var(--fw-bold);
  font-size: var(--font-size-sm);
  height: 52px;
  color: #fff;
}

.call-ctrl.end:hover {
  filter: brightness(0.92);
}

/* ─── SIDE PANEL ─── */
.call-side {
  background: rgba(255, 255, 255, 0.04);
  border-left: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: 100vh;
}

.call-side--light {
  background: #fff;
  border-left: 1px solid var(--color-gray-200);
}

.call-side-head {
  padding: var(--space-4) var(--space-5);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  gap: var(--space-5);
  flex-shrink: 0;
}

.call-side--light .call-side-head {
  border-bottom-color: var(--color-gray-200);
}

.call-side-head .tabx {
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  font-weight: 700;
  font-size: var(--font-size-sm);
}

.call-side--light .call-side-head .tabx {
  color: var(--color-gray-400);
}

.call-side-head .tabx.on {
  color: #fff;
}

.call-side--light .call-side-head .tabx.on {
  color: var(--color-primary);
}

/* ─── CHAT ─── */
.chat-body {
  flex: 1;
  padding: var(--space-5);
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  overflow-y: auto;
}

.chat-msg {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-width: 92%;
}

.chat-msg .au {
  font-size: 10px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.chat-msg .bub {
  font-size: var(--font-size-sm);
  color: #fff;
  background: rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-md);
  padding: var(--space-3) var(--space-4);
  line-height: 1.5;
}

.chat-msg.me {
  align-self: flex-end;
  align-items: flex-end;
}

.chat-msg.me .bub {
  background: var(--color-accent, #d81a67);
}

/* ─── ANEXOS (documentos da consulta) ─── */
.exam-upload-inline {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-2);
  padding-bottom: var(--space-4);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.exam-upload-inline__file-label {
  cursor: pointer;
  margin: 0;
}

.exam-upload-inline__input {
  display: none;
}

.exam-upload-inline__filename {
  font-size: var(--font-size-xs);
  color: rgba(255, 255, 255, 0.7);
  word-break: break-all;
}

.exam-upload-inline__desc {
  width: 100%;
  box-sizing: border-box;
  font-size: var(--font-size-sm);
  padding: var(--space-1) var(--space-2);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: var(--radius-sm);
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  outline: none;

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }

  &:focus {
    border-color: var(--color-primary);
  }
}

.exam-upload-inline__error {
  width: 100%;
  font-size: var(--font-size-xs);
  color: #f58c8c;
  margin: 0;
}

.attach-doc {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.attach-doc__badge {
  flex: none;
  padding: 2px 8px;
  border-radius: var(--radius-full, 999px);
  font-size: 11px;
  font-weight: var(--fw-bold, 700);
  white-space: nowrap;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
}

.attach-doc__badge--receita {
  background: rgba(26, 108, 247, 0.25);
  color: #8ab4ff;
}

.attach-doc__badge--atestado {
  background: rgba(46, 125, 50, 0.25);
  color: #7fd894;
}

.attach-doc__badge--exame {
  background: rgba(216, 26, 103, 0.25);
  color: #f58cb6;
}

.attach-doc__info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.attach-doc__title {
  font-size: var(--font-size-sm);
  color: #fff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.attach-doc__date {
  font-size: var(--font-size-xs);
  color: rgba(255, 255, 255, 0.5);
}

.attach-doc__link {
  flex: none;
  background: none;
  border: none;
  padding: 5px 15px 5px 15px;
  cursor: pointer;
  font-family: inherit;
  font-size: var(--font-size-xs);
  font-weight: var(--fw-bold, 700);
  color: #fff;
  text-decoration: underline;
}

.chat-input {
  padding: var(--space-4);
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  gap: var(--space-2);
  flex-shrink: 0;
}

.chat-input input {
  flex: 1;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 9999px;
  color: #fff;
  padding: 0 var(--space-4);
  height: 42px;
  font-family: inherit;
  font-size: var(--font-size-sm);
  outline: none;
}

.chat-input input::placeholder {
  color: rgba(255, 255, 255, 0.4);
}

.chat-input .send {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: var(--color-accent, #d81a67);
  color: #fff;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  cursor: pointer;
}

.chat-input .send svg {
  display: block;
}

/* ─── ATESTADO MODAL ─── */
.atestado-modal-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.72);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.atestado-modal {
  background: #fff;
  border-radius: 12px;
  width: 100%;
  max-width: 480px;
  padding: 24px;
  color: var(--color-gray-900, #111827);
}

.atestado-modal__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  font-weight: var(--fw-semibold, 600);
  font-size: var(--font-size-base, 16px);
}

.atestado-modal__close {
  background: none;
  border: none;
  font-size: 20px;
  border-radius: 20px;
  line-height: 1;
  padding: 1px 5px 3px 5px;
  cursor: pointer;
  color: var(--color-gray-500, #6b7280);
}

.atestado-modal__success {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 24px 0;
  font-size: var(--font-size-sm, 14px);
  color: var(--color-success, #2e7d32);
  font-weight: var(--fw-semibold, 600);
}

.atestado-form textarea,
.atestado-form input[type="text"],
.atestado-form input[type="number"],
.atestado-form input[type="date"] {
  width: 100%;
  border: 1.5px solid var(--color-gray-200, #e5e7eb);
  border-radius: var(--radius-md, 8px);
  padding: 8px 12px;
  font-size: var(--font-size-sm, 14px);
  font-family: inherit;
  color: var(--color-gray-900, #111827);
  background: #fff;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.15s;
}

.atestado-form textarea:focus,
.atestado-form input[type="text"]:focus,
.atestado-form input[type="number"]:focus,
.atestado-form input[type="date"]:focus {
  border-color: var(--color-primary, #1a6cf7);
}

.atestado-form__desc {
  min-height: 80px;
  resize: vertical;
}

.atestado-form__open-link {
  color: blue;
  cursor: pointer;
}

/* neutraliza o `a:hover { color: #4e5862 }` global (style.css) — a cor não muda */
.atestado-form__open-link:hover,
.atestado-form__open-link:focus,
.atestado-form__open-link:active {
  color: blue;
}

.atestado-form__row {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}

.atestado-form__file-label {
  display: inline-flex;
  margin: 8px 8px 0 0;
  cursor: pointer;
}

.atestado-form__file-input {
  display: none;
}

.atestado-form__filename {
  display: inline-block;
  margin-top: 8px;
  font-size: var(--font-size-xs, 12px);
  color: var(--color-gray-500, #6b7280);
  word-break: break-all;
}

.atestado-form__actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 16px;
}

.atestado-form__generate {
  display: flex;
  margin-top: 12px;
}

.atestado-form__error {
  margin: 8px 0 0;
  font-size: var(--font-size-xs, 12px);
  color: var(--color-danger, #d92d20);
}

.atestado-form__generated {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 10px;
  margin: 12px 0 0;
  font-size: var(--font-size-sm, 14px);
  color: var(--color-gray-700, #374151);
}

/* Modelo do atestado — renderizado fora da tela só para virar PDF. Cores
   literais de propósito: o PDF precisa ficar igual independentemente de tema. */
.atestado-doc {
  position: fixed;
  left: -10000px;
  top: 0;
  z-index: -1;
  width: 720px;
  padding: 56px 60px 40px;
  background: #fff;
  color: #1a2233;
  font-family: Georgia, "Times New Roman", serif;
}

.atestado-doc__logo {
  display: block;
  width: 150px;
  height: auto;
  margin: 0 auto;
}

.atestado-doc__rule {
  height: 2px;
  margin-top: 20px;
  background: linear-gradient(
    90deg,
    #1a6cf7 0 40%,
    #d81a67 40% 55%,
    #dbe0ea 55%
  );
}

.atestado-doc__title {
  margin: 34px 0 26px;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-align: center;
}

.atestado-doc__body {
  margin: 0 0 20px;
  font-size: 15px;
  line-height: 1.9;
  text-align: justify;
}

.atestado-doc__field {
  margin: 0 0 12px;
  font-size: 14px;
  line-height: 1.7;
}

.atestado-doc__label {
  display: inline-block;
  margin-right: 6px;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: #5b6472;
}

.atestado-doc__date {
  margin: 28px 0 44px;
  font-size: 14px;
}

.atestado-doc__sign {
  text-align: center;
}

.atestado-doc__sign-name {
  display: inline-block;
  padding: 10px 40px 0;
  border-top: 1px solid #1a2233;
  font-size: 14.5px;
  font-weight: 700;
}

.atestado-doc__sign-role {
  display: block;
  margin-top: 4px;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 11.5px;
  color: #5b6472;
}

.atestado-doc__foot {
  margin-top: 40px;
  padding-top: 14px;
  border-top: 1px solid #dbe0ea;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 10px;
  color: #5b6472;
  text-align: center;
}

@keyframes blink {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
}
</style>
